import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Plus, User } from "react-feather";
import { useSelector } from "react-redux";
import { validateFieldUserName } from "../../api/employeeApi";
import { getOtherRefSearchDropdown } from "../../api/keyvalue";
import {
  agreementByStkId,
  isStakeholderUserEnable,
} from "../../api/stakeholderapi";
import { getAgreementTemplateList } from "../../api/agreementTemplateApi";
import { FORMS, MODULES } from "../../const";
import {
  formatFromJsonDate,
  formatToDateOnlyFromJsonDate,
} from "../../dateConfig";
import { emptyValidator } from "../../validators";
import InfoList from "../InfoList";
import LoadingSpinner from "../LoadingSpinner";
import Button from "../input/Button";
import CheckBox from "../input/CheckBox";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import SearchDropdown from "../input/SearchDropdown";
import { stakeholderValidation } from "../validations/stakeholderCreation";
import { toast } from "react-toastify";
import { errorMessageToast } from "../../helper";
import { getFixedFormSchemaData } from "../../api/form-generator-api";
import FixedFormTab from "../form-generator/fixed/FixedFormTab";

const FORM = FORMS.STAKEHOLDER.STK_DETAIL.form;

const typeTemplateMap = {
  1: "REQUESTING_CUSTOMER_STAKEHOLDER",
  2: "SHIPPER_STAKEHOLDER",
  3: "CONSIGNEE_STAKEHOLDER",
  4: "TRANSPORTER_STAKEHOLDER",
  5: "SUPPLIER_STAKEHOLDER",
  6: "AGENT_STAKEHOLDER",
  7: "INV_CUSTOMER_STAKEHOLDER",
  8: "BILL_CUSTOMER_STAKEHOLDER",
  9: "VENDOR_STAKEHOLDER",
};

const StakeholderForm = forwardRef(
  (
    { errors, dropdown, isLoading, currentValues, editMode, stkTypeList },
    ref,
  ) => {
    const [mFields, setMFields] = useState({});
    const [dataLoading, setDataLoading] = useState(true);
    const [templateData, setTemplateData] = useState({});
    const [templateOptions, setTemplateOptions] = useState([]);
    const mandatoryFields = useSelector(
      (state) => state.stakeholder.stakeholderForm,
    );

    useEffect(() => {
      const fetchTemplates = async () => {
        try {
          const res = await getAgreementTemplateList();
          if (res) {
            const data = Array.isArray(res) ? res : res.data;
            if (data && Array.isArray(data)) {
              setTemplateOptions(
                data.map((item) => ({
                  key: item.agtId,
                  value: item.agtId,
                  text: item.agtTitle || item.agtCode,
                })),
              );
            }
          }
        } catch (error) {
          console.error("Failed to fetch templates", error);
        }
      };
      fetchTemplates();
    }, []);

    useEffect(() => {
      if (Object.entries(mandatoryFields).length <= 0)
        setMFields({
          stkName: {
            field: "stkName",
            name: "Name",
            required: true,
          },
          stkGroups: {
            field: "stkGroup",
            name: "Group",
            required: true,
          },
          stkStatus: {
            field: "stkStatus",
            name: "Status",
            required: true,
          },
          stkActive: {
            field: "stkActive",
            name: "Active",
            required: true,
          },
        });
      else setMFields(mandatoryFields);
    }, [mandatoryFields]);
    useEffect(() => {
      (async () => {
        setDataLoading(true);
        if (stkTypeList.length === 1) {
          const type = stkTypeList[0];
          const template = typeTemplateMap[type];
          const data = await getFixedFormSchemaData(template);
          setTemplateData(data);
        } else {
          const data = await getFixedFormSchemaData("MULTI_STAKEHOLDER");
          setTemplateData(data);
        }
        setDataLoading(false);
      })();
    }, [stkTypeList]);

    const [currentErrors, setCurrentErrors] = useState(errors);
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [agrDetailsLoading, setAgrDetailsLoading] = useState(false);
    const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
    const isTransporter = Array.isArray(stkTypeList) && stkTypeList.includes(4); // 4 = TRANSPORTER_STAKEHOLDER

    const [initialActive, setInitialActive] = useState(
      editMode ? currentValues?.stkActive : 1,
    );

    const chargeTypeSelect = async (e, data) => {
      setStakeholderData((prev) => ({
        ...prev,
        stkChargeType: data.value,
      }));
    };

    const shipmentTypeSelect = async (e, data) => {
      setStakeholderData((prev) => ({
        ...prev,
        stkShipmentType: data.value,
      }));
    };

    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    const [previousCharges, setPreviousCharges] = useState([]);
    const [previousShipments, setPreviousShipments] = useState([]);

    useEffect(() => {
      if (editMode) {
        const charges = currentValues.chargeTypeResponseList || [];
        setPreviousCharges(charges);

        const shipments = currentValues.shipmentTypeResponseList || [];
        setPreviousShipments(shipments);

        setStakeholderData((prev) => ({
          ...prev,
          stkChargeType: charges,
          stkShipmentType: shipments,
        }));
      }
    }, [currentValues, editMode]);

    const [stakeholderData, setStakeholderData] = useState(
      editMode
        ? {
            ...currentValues,
            stkBirthDate: currentValues.stkBirthDate
              ? formatToDateOnlyFromJsonDate(currentValues.stkBirthDate)
              : "",
            stkGroups: currentValues.stkGroupsId,
            stkAgreement: currentValues.stkAgreementId,
            stkAgreementTemplate: currentValues.stkAgreementTemplate,
            stkChargeType: currentValues.chargeTypeResponseList
              ? currentValues.chargeTypeResponseList.map(
                  (item) => item.sctChargeType,
                )
              : [],
          }
        : {
            stkReference: "",
            stkPrefix: "",
            stkChild: null,
            stkParentStkId: null,
            stkMainCategory: null,
            stkMidCategory: null,
            stkName: "",
            stkGroups: null,
            stkExportLicence: "",
            stkIataCode: "",
            stkNominee: "",
            stkAgreement: null,
            stkAgreementTemplate: null,
            stkSalesPerson: null,
            stkGPSProvider: null,
            stkStatus: 2,
            stkActive: 1,
            stkChargeType: [],
            stkUserEnable: false,
            stkUserName: "",
            stkBrNumber: "",
            stkBirthDate: "",
            stkCompliance: null,
          },
    );

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const addedValues = stakeholderData.stkChargeType.filter(
            (item) => !previousCharges.some((charges) => charges === item),
          );
          const deletedValues = previousCharges.filter(
            (item) => !stakeholderData.stkChargeType.includes(item),
          );

          const addedShipmentTypes = stakeholderData.stkShipmentType.filter(
            (item) => !previousShipments.some((ship) => ship === item),
          );
          const deletedShipmentTypes = previousShipments.filter(
            (item) => !stakeholderData.stkShipmentType.includes(item),
          );

          // const created = addedValues;

          return {
            ...stakeholderData,
            stakeholderChargeTypeRequestList: {
              created: addedValues,
              deleted: deletedValues,
              modified: [],
            },
            stakeholderShipmentTypeRequestList: {
              created: addedShipmentTypes,
              deleted: deletedShipmentTypes,
              modified: [],
            },
          };
        } else {
          return {
            ...stakeholderData,
            stakeholderChargeTypeRequestList: stakeholderData.stkChargeType,
            stakeholderShipmentTypeRequestList: stakeholderData.stkShipmentType,
          };
        }
      },
      isDeletedActive: () =>
        stakeholderData.stkActive === 2 && initialActive !== 2,
      resetForm: () => {
        setStakeholderData({
          stkReference: "",
          stkPrefix: "",
          stkChild: null,
          stkParentStkId: null,
          stkMainCategory: null,
          stkMidCategory: null,
          stkName: "",
          stkGroups: null,
          stkExportLicence: "",
          stkIataCode: "",
          stkNominee: "",
          stkAgreement: null,
          stkAgreementTemplate: null,
          stkSalesPerson: null,
          stkGPSProvider: null,
          stkStatus: 2,
          stkActive: 1,
          stkChargeType: [],
          stkCompliance: null,
        });
        setSearchFieldReset(true);
      },
      validateForm: () => {
        let validationState = {};

        // for (const [key, value] of Object.entries(mFields)) {
        //     validationState[key] = {
        //         validators: [emptyValidator()],
        //         name: value.name,
        //     };
        // }
        for (const [key, value] of Object.entries(mFields)) {
          validationState[key] = {
            validators: value.required ? [emptyValidator()] : [],
            name: value.name,
          };
        }

        return stakeholderValidation(stakeholderData, validationState);
      },
    }));

    const [stkUserEnable, setStkUserEnable] = useState(false);

    useEffect(() => {
      (async () => {
        if (!stakeholderData?.stkName) return;

        const getFirstName = () =>
          stakeholderData.stkName.toLowerCase().split(" ")[0];

        const getFullName = () =>
          stakeholderData.stkName.toLowerCase().replace(/\s/g, "");

        const prefix = isTransporter ? "TP" : (stakeholderData.stkPrefix || "STK");

        const isUserNameAvailable = async (username) =>
          await validateFieldUserName("username", username, 1);

        const generateUserName = async () => {
          let baseUserName = getFirstName() + prefix;
          let finalUserName = "";

          if (!(await isUserNameAvailable(baseUserName))) {
            finalUserName = baseUserName;
          } else {
            const altUserName = getFullName() + prefix;
            if (!(await isUserNameAvailable(altUserName))) {
              finalUserName = altUserName;
            } else {
              let attempts = 0;
              while (attempts < 5) {
                const candidate = `${baseUserName}-${getRandomInt(9, 99)}`;
                if (!(await isUserNameAvailable(candidate))) {
                  finalUserName = candidate;
                  break;
                }
                attempts++;
              }
              // if (!finalUserName) finalUserName = baseUserName;
            }
          }

          setStakeholderData((prev) => ({
            ...prev,
            stkUserName: finalUserName,
          }));
        };

        if (stakeholderData.stkUserEnable === false) {
          setStakeholderData((prev) => ({ ...prev, stkUserName: "" }));
          setUsernameManuallyEdited(false);
          return;
        }

        setUsernameManuallyEdited(false);
        await generateUserName();
      })();
    }, [stakeholderData.stkUserEnable]);

    const getRandomInt = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    useEffect(() => {
      (async () => {
        if (Object.keys(stkTypeList).length > 0) {
          const stkUser = await isStakeholderUserEnable(stkTypeList);
          setStkUserEnable(stkUser);
        }
      })();
    }, [stkTypeList]);

    const agreementByStakeholder = async () => {
      try {
        setAgrDetailsLoading(true);
        const result = await agreementByStkId(stakeholderData?.stkId);
        if (!result.data || !result.data === null) {
          toast.error("Agreement not found", {
            autoClose: 5000,
            containerId: "Main",
          });
          setAgrDetailsLoading(false);
          return;
        }
        setStakeholderData((prevState) => ({
          ...prevState,
          stkAgreement: result?.data?.agrId,
          agrCode: result?.data?.agrCode,
          agrOwnerName: result?.data?.agrOwnerName,
          agrOwnerNic: result?.data?.agrOwnerNic,
          agrOwnerContactNo: result?.data?.agrOwnerContactNo,
          agrFromDate: formatFromJsonDate(result?.data?.agrFromDate),
          agrToDate: formatFromJsonDate(result?.data?.agrToDate),
        }));
        setAgrDetailsLoading(false);
      } catch (e) {
        // errorMessageToast(e);
        console.error(e);
        toast.error("Agreement not found", {
          autoClose: 5000,
          containerId: "Main",
        });
        setAgrDetailsLoading(false);
        return;
      } finally {
        setAgrDetailsLoading(false);
      }
    };

    const fieldMap = {
      // --- Stakeholder core info ---
      STK_INFO_CODE: (
        <Input
          label="Stakeholder code"
          error={currentErrors.stkCode}
          module={MODULES.Master}
          form={FORM}
          field="stk_code"
          editMode={editMode}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkCode: _, ...rest } = prevState;
              return rest;
            })
          }
          name="stakeholderCode"
          value={stakeholderData.stkCode}
          disabled={true}
        />
      ),

      STK_INFO_PREFIX: (
        <Input
          required={mFields.stkPrefix?.required}
          label="Prefix"
          error={currentErrors.stkPrefix}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkPrefix: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_reference"
          editMode={editMode}
          name="reference"
          value={stakeholderData.stkPrefix}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkPrefix: e.target.value,
            }));
          }}
        />
      ),

      STK_INFO_REFERENCE: (
        <Input
          label="Reference"
          error={currentErrors.stkReference}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkReference: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_reference"
          editMode={editMode}
          name="reference"
          value={stakeholderData.stkReference}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkReference: e.target.value,
            }));
          }}
        />
      ),

      STK_INFO_HOLDING_COMPANY: (
        <CheckBox
          label="Holding company"
          checked={stakeholderData.stkChild}
          onChange={() => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkChild: !prevState.stkChild,
            }));
          }}
          module={MODULES.Master}
          form={FORM}
          field="stk_child"
          authenticated={true}
          editMode={editMode}
          error={currentErrors.stkChild}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkChild: _, ...rest } = prevState;
              return rest;
            })
          }
        />
      ),

      STK_INFO_PARENT_COMPANY: (
        <SearchDropdown
          key={1}
          editMode={editMode}
          module={MODULES.Master}
          form={FORM}
          field="stk_perent_stk_id"
          reset={searchFieldReset}
          disabled={!stakeholderData.stkChild}
          setReset={setSearchFieldReset}
          clearable={true}
          onClear={() => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkParentStkId: null,
            }));
          }}
          onResultSelect={(data) =>
            setStakeholderData((prevState) => ({
              ...prevState,
              stkParentStkId: data,
            }))
          }
          api={getOtherRefSearchDropdown}
          apiDependency={{
            type: "stakeholder_type",
            value: "Customer",
          }}
          fieldInfo={{
            tableName: "masterdata.tms_m_stakeholder",
            primaryKey: "stk_id",
            fieldName: "stk_name",
            id: stakeholderData.stkParentStkId,
          }}
          label="Stakeholder(Parent Company)"
          error={currentErrors.stkParentStkId}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkParentStkId: _, ...rest } = prevState;
              return rest;
            })
          }
        />
      ),

      STK_INFO_CATEGORY_1: (
        <Dropdown
          label="Category 1"
          error={currentErrors.stkMainCategory}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkMainCategory: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_main_category"
          editMode={editMode}
          placeholder="Category 1"
          options={dropdown.cat1Options}
          value={stakeholderData.stkMainCategory}
          style={{ height: "20px" }}
          onChange={(e, data) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkMainCategory: data.value,
            }));
          }}
        />
      ),

      STK_INFO_CATEGORY_2: (
        <Dropdown
          label="Category 2"
          module={MODULES.Master}
          form={FORM}
          field="stk_mid_category"
          editMode={editMode}
          error={currentErrors.stkMidCategory}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkMidCategory: _, ...rest } = prevState;
              return rest;
            })
          }
          placeholder="Category 2"
          options={dropdown.cat2Options}
          value={stakeholderData.stkMidCategory}
          style={{ height: "20px" }}
          onChange={(e, data) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkMidCategory: data.value,
            }));
          }}
        />
      ),

      STK_INFO_NAME: (
        <Input
          required={mFields.stkName?.required}
          label="Name"
          error={currentErrors.stkName}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkName: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_name"
          companyWise={true}
          companyCol="stk_com_id"
          activeCol="stk_active"
          name="name"
          placeholder="Name"
          // disabled={!editMode && isTransporter && usernameManuallyEdited}
          value={stakeholderData.stkName}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkName: e.target.value,
            }));
          }}
          duplicate={false}
          tableName="masterdata.tms_m_stakeholder"
          primaryKey="stk_id"
          searchField="stk_name"
          keyType="S"
          duplicateErrorMessage="This Stakeholder name already exists"
          setErrors={setCurrentErrors}
          errorField="stkName"
          id={stakeholderData.stkId}
        />
      ),

      STK_INFO_GROUP: (
        <SearchDropdown
          key={1}
          editMode={editMode}
          reset={searchFieldReset}
          setReset={setSearchFieldReset}
          clearable={true}
          module={MODULES.Master}
          form={FORM}
          field="stk_groups"
          onClear={() => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkGroups: null,
            }));
          }}
          onResultSelect={(data) =>
            setStakeholderData((prevState) => ({
              ...prevState,
              stkGroups: data,
            }))
          }
          api={getOtherRefSearchDropdown}
          apiDependency={{ type: "stake_group" }}
          fieldInfo={{
            tableName: "masterdata.tms_m_stake_groups",
            primaryKey: "grp_id",
            fieldName: "grp_group",
            id: stakeholderData.stkGroups,
          }}
          required={mFields.stkGroups?.required}
          label="Group"
          error={currentErrors.stkGroups}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkGroups: _, ...rest } = prevState;
              return rest;
            })
          }
        />
      ),

      STK_INFO_EXPORT_LICENCE: (
        <Input
          label="Export Licence"
          error={currentErrors.stkExportLicence}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkExportLicence: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_export_licence"
          editMode={editMode}
          name="exportLicence"
          placeholder="Export Licence"
          value={stakeholderData.stkExportLicence}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkExportLicence: e.target.value,
            }));
          }}
        />
      ),

      STK_INFO_IATA_CODE: (
        <Input
          label="IATA Code"
          error={currentErrors.stkIataCode}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkIataCode: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_iata_code"
          editMode={editMode}
          name="iataCode"
          placeholder="IATA Code"
          value={stakeholderData.stkIataCode}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkIataCode: e.target.value,
            }));
          }}
        />
      ),

      STK_INFO_BR_NUMBER: (
        <Input
          label="BR Number"
          error={currentErrors.stkBrNumber}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkBrNumber: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_brnumber"
          editMode={editMode}
          name="stkBrNumber"
          placeholder="BR Number"
          value={stakeholderData.stkBrNumber}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkBrNumber: e.target.value,
            }));
          }}
        />
      ),

      STK_COMPLIANCE: (
        <Dropdown
          required={mFields.stkCompliance?.required}
          label="Compliance Status"
          module={MODULES.Master}
          form={FORM}
          field="stk_compliance"
          editMode={editMode}
          error={currentErrors.stkCompliance}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkCompliance: _, ...rest } = prevState;
              return rest;
            })
          }
          placeholder="Compliance Status"
          options={dropdown.compliance}
          style={{ height: "20px" }}
          value={stakeholderData.stkCompliance}
          onChange={(e, data) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkCompliance: data.value,
            }));
          }}
        />
      ),

      STK_INFO_BIRTH_DATE: (
        <Input
          label="Birth Date"
          error={currentErrors.stkBirthDate}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkBirthDate: _, ...rest } = prevState;
              return rest;
            })
          }
          module={MODULES.Master}
          form={FORM}
          field="stk_birth_date"
          editMode={editMode}
          type="date"
          name="stkBirthDate"
          placeholder="Birth Date"
          value={stakeholderData.stkBirthDate}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkBirthDate: e.target.value,
            }));
          }}
        />
      ),

      STK_INFO_NOMINEE: (
        <Input
          label="Nominee"
          module={MODULES.Master}
          form={FORM}
          field="stk_nominee"
          editMode={editMode}
          error={currentErrors.stkNominee}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkNominee: _, ...rest } = prevState;
              return rest;
            })
          }
          name="nominee"
          placeholder="Nominee"
          value={stakeholderData.stkNominee}
          onChange={(e) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkNominee: e.target.value,
            }));
          }}
        />
      ),

      // --- Agreement section (flattened) ---
      AGREEMENT_SEGMENT: (
        <div
          className="ui segment"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="ui two fields">
            <Input
              label="Agreement"
              module={MODULES.Master}
              form={FORM}
              field="stk_agreement"
              editMode={editMode}
              error={currentErrors.stkAgreement}
              resetError={() =>
                setCurrentErrors((prevState) => {
                  const { stkAgreement: _, ...rest } = prevState;
                  return rest;
                })
              }
              name="stkAgreement"
              value={stakeholderData.agrCode}
              onChange={(e) => {
                setStakeholderData((prevState) => ({
                  ...prevState,
                  stkAgreement: e.target.value,
                }));
              }}
              readOnly={true}
              placeholder={agrDetailsLoading ? "Loading..." : ""}
            />

            <Button
              style={{ marginTop: "22px" }}
              Icon={Plus}
              module={MODULES.Master}
              form={FORM}
              field="stk_agreement"
              editMode={editMode}
              authenticated={true}
              disabled={!editMode}
              isLoading={agrDetailsLoading}
              onClick={() => {
                agreementByStakeholder();
              }}
            />

            {/*<Dropdown*/}
            {/*  disabled={stakeholderData.stkAgreement === null}*/}
            {/*  required={true}*/}
            {/*  editMode={editMode}*/}
            {/*  label="Agreement Templates"*/}
            {/*  placeholder="Select a template"*/}
            {/*  options={templateOptions}*/}
            {/*  value={stakeholderData.stkAgreementTemplate}*/}
            {/*  onChange={(e, data) => */}
            {/*    setStakeholderData((prevState) => ({*/}
            {/*      ...prevState,*/}
            {/*      stkAgreementTemplate: data.value,*/}
            {/*    }))*/}
            {/*  }*/}
            {/*  error={currentErrors.stkAgreementTemplate}*/}
            {/*  resetError={() =>*/}
            {/*    setCurrentErrors((prevState) => {*/}
            {/*      const { stkAgreementTemplate: _, ...rest } = prevState;*/}
            {/*      return rest;*/}
            {/*    })*/}
            {/*  }*/}
            {/*/>*/}
          </div>

          <div>
            <div
              style={{
                marginBottom: "1rem",
                display: "flex",
                justifyContent: "space-between",
                width: "50%",
              }}
            >
              <div>
                {stakeholderData.agrOwnerName && (
                  <p>
                    <strong>Owner Name : </strong>{" "}
                    {stakeholderData.agrOwnerName
                      ? stakeholderData.agrOwnerName
                      : "N/A"}
                  </p>
                )}
                {stakeholderData.agrOwnerNic && (
                  <p>
                    <strong>Owner NIC : </strong>{" "}
                    {stakeholderData.agrOwnerNic
                      ? stakeholderData.agrOwnerNic
                      : "N/A"}
                  </p>
                )}
                {stakeholderData.agrOwnerContactNo && (
                  <p>
                    <strong>Owner Contact No : </strong>{" "}
                    {stakeholderData.agrOwnerContactNo
                      ? stakeholderData.agrOwnerContactNo
                      : "N/A"}
                  </p>
                )}
              </div>

              <div>
                {stakeholderData.agrFromDate && (
                  <p>
                    <strong>From Date : </strong>{" "}
                    {stakeholderData.agrFromDate
                      ? formatFromJsonDate(stakeholderData.agrFromDate)
                      : "N/A"}
                  </p>
                )}
                {stakeholderData.agrToDate && (
                  <p>
                    <strong>To Date : </strong>
                    {stakeholderData.agrToDate
                      ? formatFromJsonDate(stakeholderData.agrToDate)
                      : "N/A"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ),

      // --- Assignments (flattened) ---
      STK_SALES_PERSON: (
        <SearchDropdown
          key={1}
          editMode={editMode}
          module={MODULES.Master}
          form={FORM}
          field="stk_sales_person"
          reset={searchFieldReset}
          setReset={setSearchFieldReset}
          clearable={true}
          onClear={() => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkSalesPerson: null,
            }));
          }}
          onResultSelect={(data) =>
            setStakeholderData((prevState) => ({
              ...prevState,
              stkSalesPerson: data,
            }))
          }
          api={getOtherRefSearchDropdown}
          apiDependency={{ type: "employee", value: "" }}
          fieldInfo={{
            tableName: "masterdata.tms_m_employee",
            primaryKey: "emp_id",
            fieldName: "emp_fullname",
            id: stakeholderData.stkSalesPerson,
          }}
          label="Sales Person"
          error={currentErrors.stkSalesPerson}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkSalesPerson: _, ...rest } = prevState;
              return rest;
            })
          }
        />
      ),

      STK_GPS_PROVIDER: (
        <SearchDropdown
          key={1}
          editMode={editMode}
          module={MODULES.Master}
          form={FORM}
          field="stk_gps_provider"
          reset={searchFieldReset}
          setReset={setSearchFieldReset}
          clearable={true}
          onClear={() => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkGPSProvider: null,
            }));
          }}
          onResultSelect={(data) =>
            setStakeholderData((prevState) => ({
              ...prevState,
              stkGPSProvider: data,
            }))
          }
          api={getOtherRefSearchDropdown}
          apiDependency={{ type: "stakeholder_type", value: "Supplier" }}
          fieldInfo={{
            tableName: "masterdata.tms_m_stakeholder",
            primaryKey: "stk_id",
            fieldName: "stk_name",
            id: stakeholderData.stkGPSProvider,
          }}
          label="GPS Provider"
          error={currentErrors.stkGPSProvider}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkGPSProvider: _, ...rest } = prevState;
              return rest;
            })
          }
        />
      ),

      // --- Types (flattened) ---
      STK_CHARGE_TYPE: (
        <Dropdown
          label="Charge Type"
          search={true}
          error={currentErrors.sctChargeType}
          editMode={editMode}
          module={MODULES.Master}
          form={FORM}
          field="sct_charge_type"
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { sctChargeType: _, ...rest } = prevState;
              return rest;
            })
          }
          multiple={true}
          options={dropdown.chargeTypeOptions}
          placeholder="Select Charge Types"
          style={{ height: "20px" }}
          value={stakeholderData.stkChargeType}
          onChange={chargeTypeSelect}
        />
      ),

      STK_SHIPMENT_TYPE: (
        <Dropdown
          label="Shipment Type"
          search={true}
          error={currentErrors.srsShipmentType}
          editMode={editMode}
          module={MODULES.Master}
          form={FORM}
          field="srs_request_shipments"
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { srsShipmentType: _, ...rest } = prevState;
              return rest;
            })
          }
          multiple={true}
          options={dropdown.shipmentTypesOptions}
          placeholder="Select Shipment Types"
          style={{ height: "20px" }}
          value={stakeholderData.stkShipmentType}
          onChange={shipmentTypeSelect}
        />
      ),

      // --- User creation (flattened) ---
      STK_USER_SECTION:
        !editMode && stkUserEnable ? (
          <div className="ui segment">
            <>
              <h4 className="ui dividing header">
                Create a user account for this stakeholder{" "}
              </h4>
              <div className="two fields">
                <CheckBox
                  label={"Stakeholder User"}
                  checked={stakeholderData.stkUserEnable}
                  onChange={() => {
                    setStakeholderData((prev) => ({
                      ...prev,
                      stkUserEnable: !prev.stkUserEnable,
                    }));
                  }}
                  disabled={stakeholderData.stkName === "" || editMode}
                />

                <Input
                  label="User Name"
                  error={currentErrors.stkUserName}
                  module={MODULES.Master}
                  form={FORM}
                  field="stk_username"
                  placeholder={
                    stakeholderData.stkUserEnable
                      ? "Loading ..."
                      : "Enter User Name"
                  }
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { stkUserName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="stkUserName"
                  value={stakeholderData.stkUserName}
                  onChange={(e) => {
                    const sanitizedValue = e.target.value.replace(/\s/g, "");
                    setStakeholderData((prevState) => ({
                      ...prevState,
                      stkUserName: sanitizedValue,
                    }));
                    if (isTransporter) {
                      setUsernameManuallyEdited(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  disabled={
                    stakeholderData.stkUserEnable === false ||
                    stakeholderData.stkName === ""
                  }
                />
              </div>
            </>
          </div>
        ) : null,

      // --- Status (flattened) ---
      STK_STATUS: (
        <Dropdown
          required={mFields.stkStatus?.required}
          label="Status"
          module={MODULES.Master}
          form={FORM}
          field="stk_status"
          editMode={editMode}
          error={currentErrors.stkStatus}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkStatus: _, ...rest } = prevState;
              return rest;
            })
          }
          placeholder="Status"
          options={dropdown.statusOptions}
          style={{ height: "20px" }}
          value={stakeholderData.stkStatus}
          onChange={(e, data) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkStatus: data.value,
            }));
          }}
        />
      ),

      STK_ACTIVE: (
        <Dropdown
          required={mFields.stkActive?.required}
          label="Active"
          module={MODULES.Master}
          form={FORM}
          field="stk_active"
          authenticated={true}
          needDeletePermission={true}
          editMode={editMode}
          error={currentErrors.stkActive}
          resetError={() =>
            setCurrentErrors((prevState) => {
              const { stkActive: _, ...rest } = prevState;
              return rest;
            })
          }
          placeholder="Active"
          options={dropdown.activeOptions}
          style={{ height: "20px" }}
          value={stakeholderData.stkActive}
          onChange={(e, data) => {
            setStakeholderData((prevState) => ({
              ...prevState,
              stkActive: data.value,
            }));
          }}
        />
      ),
    };
    return (
      <>
        {isLoading || dataLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="ui segment">
            <h4 className="ui dividing header">Stakeholder Types</h4>
            <FixedFormTab
              tab={templateData?.template?.STAKEHOLDER_INFO ?? {}}
              fieldMap={fieldMap}
            />
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: stakeholderData.createdBy
                      ? stakeholderData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: stakeholderData.createdDate
                      ? formatFromJsonDate(stakeholderData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: stakeholderData.lastModifiedBy
                      ? stakeholderData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: stakeholderData.lastModifiedDate
                      ? formatFromJsonDate(stakeholderData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </div>
        )}
      </>
    );
  },
);

export default StakeholderForm;
